import { useDraft, type AttachedDoc, type Draft, type DraftMedia } from './draft';

/**
 * The draft, put away for the trip to Stripe and brought back after it.
 *
 * The draft is memory (state/draft.ts), and that was a choice: a half-described app is not worth
 * restoring across sessions. The trial checkout breaks the assumption behind it. The owner's
 * funnel takes the card right before the build (2026-09-25), and checkout is a full page
 * navigation, so without this somebody who had just given their card came back to an empty
 * form — the worst possible first minute. The draft itself stays memory; this is one snapshot,
 * taken as Build sends them to Stripe and read back on the two pages Stripe returns to.
 *
 * Where it goes:
 *  - The CHOICES (the idea, the plan with its features, layout and look, the kit, the name…) go
 *    to sessionStorage. It is per tab, it survives a same-tab trip to another site and back,
 *    and it dies with the tab — which keeps "not across sessions" true.
 *  - The DOCUMENT (up to 4 MB, MAX_DOCUMENT_BYTES) and the FILES go to IndexedDB, keyed by an
 *    id that lives in that same sessionStorage record, so another tab never picks them up.
 *    sessionStorage holds about 5 MB of text and a File is not text.
 *
 * IndexedDB can be missing or refuse — a private window, a full disk, some in-app browsers, and
 * ads open in in-app browsers. Then the document is tried beside the choices, and whatever
 * could not be kept is NAMED in the result, so the page can say so before the redirect and on
 * return. An app quietly built without the picture somebody chose is worse than being told.
 */

const SESSION_KEY = 'saddle.draftStash';
const DB_NAME = 'saddle-draft';
const STORE = 'files';
/*
 * A day. Stripe's session lasts a day too, and a return after that is a new visit: the stash is
 * only read on the pages Stripe returns to, but a plan from last week reappearing there would
 * be the stale-plan bug draft.ts was written to avoid.
 */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * The draft's fields that are not choices: setters, and `tailoring` — a /plan request in
 * flight, which cannot outlive the page that sent it. The attachments go their own way.
 *
 * `pendingBook` goes nowhere: a long text still waiting to say where it came from is not a
 * finished choice, and it can be 4 MB. The plan warns before its step is left with one.
 *
 * The page documents (`attachedDocs`, D1) DO travel with the choices: 40,000 characters at most,
 * which sessionStorage holds easily, and a menu that came back from Stripe missing would build a
 * café page with no prices on it.
 */
const NOT_STASHED = new Set(['tailoring', 'attachedDoc', 'attachedMedia', 'pendingBook']);

type Choices = Partial<Omit<Draft, 'tailoring' | 'attachedDoc' | 'attachedMedia' | 'pendingBook'>>;

interface SessionRecord {
  v: 1;
  id: string;
  savedAt: number;
  ownerId: string;
  choices: Choices;
  /** Where the document and files went: IndexedDB, nowhere (there were none), or here. */
  files: 'idb' | 'none' | 'inline';
  /** Only when IndexedDB refused and it fitted. */
  doc?: AttachedDoc | null;
  /** The names of what was attached, so what could not come back can be named on return. */
  attachments: string[];
  /** What was known lost when it was put away. */
  lost: string[];
}

interface FileRecord {
  id: string;
  savedAt: number;
  doc: AttachedDoc | null;
  media: {
    name: string;
    contentType: string;
    type: string;
    lastModified: number;
    bytes: ArrayBuffer;
    /** The line under the photo (D2) — absent when nothing was said, kept absent on return. */
    description?: string;
    /** The describer's pick for the top of the page; only a real `true` is stored or read back. */
    hero?: true;
  }[];
}

export interface StashResult {
  /** False when the browser kept nothing at all: the plan will not survive the trip. */
  saved: boolean;
  /** The document's title and each file's name that will NOT come back. */
  lost: string[];
}

/*
 * Opened per call and closed after it, never cached: this runs twice per checkout, and a cached
 * handle is one more thing another tab's upgrade can close from under us (platform/indexedDb.ts).
 * Its own database, so the project store's version never has to move for it.
 */
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Could not open local storage.'));
    req.onblocked = () => reject(new Error('Local storage is busy in another tab.'));
  });
}

/** Resolves when the transaction COMMITS: a request can succeed and its transaction still abort on quota. */
async function inStore<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    return await new Promise<T | undefined>((resolve, reject) => {
      tx.oncomplete = () => resolve(req ? req.result : undefined);
      tx.onerror = () => reject(tx.error ?? new Error('Local storage failed.'));
      tx.onabort = () => reject(tx.error ?? new Error('Local storage was aborted.'));
    });
  } finally {
    db.close();
  }
}

function readSession(): SessionRecord | null {
  try {
    const record = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? 'null') as SessionRecord | null;
    return record && record.v === 1 && typeof record.id === 'string' ? record : null;
  } catch {
    return null;
  }
}

function writeSession(record: SessionRecord): boolean {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}

/*
 * The file's bytes through FileReader rather than Blob.arrayBuffer(): the reader is in every
 * browser this is opened in, in-app browsers included, and in the test DOM, which has no
 * arrayBuffer() on a File.
 */
function readBytes(file: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('could not read that file'));
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.readAsArrayBuffer(file);
  });
}

const newId = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

/** The draft's choices: everything it holds except setters and what NOT_STASHED names. */
function choicesOf(state: Draft): Choices {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(state)) {
    if (typeof value === 'function' || NOT_STASHED.has(key)) continue;
    out[key] = value;
  }
  /*
   * The plan Build was pressed on IS the plan. A /plan still out when the page left cannot land
   * on the next one, and the plan screen, reopened after a cancelled checkout, would buy it again
   * (it is charged before it answers) and offer it over choices already made. Recorded as the
   * tailoring for this idea, nothing more is bought.
   */
  if (state.plan && state.tailored?.idea !== state.description) {
    out.tailored = { idea: state.description, plan: state.plan };
  }
  return out as Choices;
}

/** Drop other tabs' leftovers once they are too old to be anybody's checkout. */
async function pruneOld(keep: string): Promise<void> {
  const all = (await inStore<FileRecord[]>('readonly', (s) => s.getAll() as IDBRequest<FileRecord[]>)) ?? [];
  const stale = all.filter((r) => r.id !== keep && Date.now() - r.savedAt > MAX_AGE_MS);
  if (stale.length) await inStore('readwrite', (s) => void stale.forEach((r) => s.delete(r.id)));
}

/**
 * Put the draft away, as Build sends somebody to Stripe. Never throws: a stash that fails must
 * not stop somebody paying, but it must be SAID, which is what the result is for.
 */
export async function stashDraft(ownerId: string): Promise<StashResult> {
  const state = useDraft.getState();
  const doc = state.attachedDoc;
  const media = state.attachedMedia;
  const attachments = [...(doc ? [doc.title] : []), ...media.map((m) => m.name)];
  // The same tab's earlier stash is replaced, not added to: one checkout in flight per tab.
  const previous = readSession();
  const id = previous?.id ?? newId();
  const savedAt = Date.now();
  const base: SessionRecord = {
    v: 1, id, savedAt, ownerId, choices: choicesOf(state), files: 'none', attachments, lost: [],
  };

  if (!attachments.length) {
    const saved = writeSession(base);
    // The earlier stash's files, if it had any, are nobody's now: a clip can be tens of megabytes.
    if (previous?.files === 'idb') await inStore('readwrite', (s) => void s.delete(id)).catch(() => undefined);
    return { saved, lost: [] };
  }

  try {
    const record: FileRecord = {
      id,
      savedAt,
      doc,
      // Bytes, not the File: a File does not survive structured cloning everywhere, bytes do.
      media: await Promise.all(
        media.map(async (m: DraftMedia) => ({
          name: m.name,
          contentType: m.contentType,
          type: m.file.type,
          lastModified: m.file.lastModified,
          bytes: await readBytes(m.file),
          ...(m.description !== undefined ? { description: m.description } : {}),
          ...(m.hero === true ? { hero: true as const } : {}),
        })),
      ),
    };
    await inStore('readwrite', (s) => s.put(record));
    const saved = writeSession({ ...base, files: 'idb' });
    // Best-effort housekeeping; a failure here costs disk, not the checkout.
    await pruneOld(id).catch(() => undefined);
    // Choices that did not save make the files unreachable too: the id was in them.
    return saved ? { saved, lost: [] } : { saved, lost: attachments };
  } catch {
    // IndexedDB refused. The files cannot come; the document may fit beside the choices.
    const fileNames = media.map((m) => m.name);
    if (doc && writeSession({ ...base, files: 'inline', doc, lost: fileNames })) {
      return { saved: true, lost: fileNames };
    }
    const saved = writeSession({ ...base, files: 'none', lost: attachments });
    return { saved, lost: attachments };
  }
}

/**
 * Bring the draft back: on /billing/success before the build, and on the plan after a cancelled
 * checkout. Null when there is nothing for THIS account in this tab (none put away, another
 * account's, or too old). Otherwise the draft is filled and `lost` names what could not come.
 */
export async function restoreDraft(ownerId: string): Promise<{ lost: string[] } | null> {
  const record = readSession();
  /*
   * A stash put away SIGNED OUT (ownerId '') is the plan of whoever signs in next in this tab: the
   * journey is open until Build, which asks for the account and stashes first, because the code
   * arrives by email and switching to the mail app can cost a phone browser the page.
   */
  if (!record || (record.ownerId !== ownerId && record.ownerId !== '')) return null;
  if (!(Date.now() - record.savedAt <= MAX_AGE_MS)) {
    await clearDraftStash();
    return null;
  }

  let doc: AttachedDoc | null = null;
  let media: DraftMedia[] = [];
  let lost = record.lost;
  if (record.files === 'inline') {
    doc = record.doc ?? null;
  } else if (record.files === 'idb') {
    try {
      const files = await inStore<FileRecord | undefined>('readonly', (s) => s.get(record.id) as IDBRequest<FileRecord | undefined>);
      if (!files) throw new Error('gone');
      doc = files.doc;
      media = files.media.map((m) => ({
        name: m.name,
        contentType: m.contentType,
        file: new File([m.bytes], m.name, { type: m.type, lastModified: m.lastModified }),
        ...(typeof m.description === 'string' ? { description: m.description } : {}),
        ...(m.hero === true ? { hero: true as const } : {}),
      }));
    } catch {
      // Put away, and not there now: the site's data was cleared, or the browser evicted it.
      lost = record.attachments;
    }
  }

  // Onto a clean draft, so nothing half-made for another app rides along.
  useDraft.getState().reset();
  useDraft.setState({ ...record.choices, attachedDoc: doc, attachedMedia: media });
  return { lost };
}

/** Forget it — once the build it was kept for has started. Best-effort. */
export async function clearDraftStash(): Promise<void> {
  const record = readSession();
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* nothing to forget */
  }
  if (record?.files === 'idb') {
    await inStore('readwrite', (s) => void s.delete(record.id)).catch(() => undefined);
  }
}
